import React, { useState, useEffect, useRef } from 'react';
import { Alert } from '@mui/material';
import { Stepper } from '../designs/DesignDefault';
import {
  DesignDefault,
  CHECKOUT_STEPS,
  DesignViewEditSectionEnum,
} from '../designs';
import { Footer } from '../designs';
import { useCheckoutLoading } from './hooks/useCheckoutLoading';
import { useCheckoutForm } from './hooks/useCheckoutForm';
import { useCheckoutStepper } from './hooks/useCheckoutStepper';
import { useCheckoutAnalytics } from './hooks/useCheckoutAnalytics';
import { useCheckoutPayment } from './hooks/useCheckoutPayment';
import { useGiftProgress } from './hooks/useGiftProgress';
import { CheckoutHeader } from './components/CheckoutHeader';
import { CheckoutLoading } from './components/CheckoutLoading';
import { CheckoutViews } from './components/CheckoutViews';
import UpsellPopup from '../UpsellPopup';
import type { CheckoutPageProps } from './types/checkout.types';
import type { CheckoutResponseDto, PaymentMethodEnum } from '../../services/checkout/checkout.types';
import { useCheckoutConfig } from './hooks/useCheckoutConfig';
import { checkoutConfigToCssVars } from '../../services/checkoutConfig/checkoutConfig.css';



const CheckoutPage: React.FC<CheckoutPageProps & { initialEditingSection?: string | null }> = ({
  shopifyOffer,
  initialEditingSection,
  thankYouData,
  checkoutConfigOverride,
  paymentBadgesOverride,
  storeContactOverride,
}) => {
  const checkoutSteps = [...CHECKOUT_STEPS];

  const { config: checkoutConfig } = useCheckoutConfig(shopifyOffer, checkoutConfigOverride);

  // State
  const [orderResponse, setOrderResponse] = useState<CheckoutResponseDto | null>(null);
  const [paymentErrorMessage, setPaymentErrorMessage] = useState<string | null>(null);

  // Hooks
  const {
    loading,
    addLoading,
    removeLoading,
  } = useCheckoutLoading(['initial']);

  const {
    formData,
    setFormData,
    updateOrderSummary,
    createOrUpdateCartData,
    currentStep,
    cartLoading,
    concludeCart,
  } = useCheckoutForm(shopifyOffer);

  const {
    steps,
    editingSection,
    setEditingSection,
    getCurrentStepId,
    handleEditSection,
    handleStepClick,
  } = useCheckoutStepper(checkoutSteps, formData, initialEditingSection);
  const previousEditingSectionRef = useRef<DesignViewEditSectionEnum | null>(null);

  const {
    trackAddShippingInfo,
    trackAddPaymentInfo,
    trackPurchase,
  } = useCheckoutAnalytics(shopifyOffer, formData, shopifyOffer.storeId);

  const giftItems = (formData.shopifyOffer ?? shopifyOffer)?.items ?? shopifyOffer?.items ?? [];
  const baseItems = giftItems.filter((i) => i.source !== 'gift');
  const baseSubtotal = baseItems.reduce((sum, i) => sum + Number(i.price || 0) * (i.quantity || 1), 0);
  const baseItemCount = baseItems.reduce((sum, i) => sum + (i.quantity || 1), 0);
  const giftStates = useGiftProgress({
    gifts: (formData.shopifyOffer ?? shopifyOffer)?.gifts ?? shopifyOffer?.gifts ?? null,
    baseSubtotal,
    baseItemCount,
    appliedCouponCode: formData.orderSummary?.couponCode ?? null,
  });

  const {
    handleCouponApply,
    handlePaymentMethodChange,
    handlePlaceOrder,
    handleSaveSection,
    handleOrderBumpsChange,
    onCardElementReady,
    onReusableToken,
    upsellState,
    handleUpsellAccept,
    handleUpsellDecline,
  } = useCheckoutPayment(
    formData,
    shopifyOffer,
    steps,
    setFormData,
    setOrderResponse,
    setEditingSection,
    addLoading,
    removeLoading,
    trackAddShippingInfo,
    trackAddPaymentInfo,
    trackPurchase,
    checkoutSteps,
    createOrUpdateCartData,
    concludeCart
  );

  // Ponte mínima para permitir que o hook de pagamento sinalize o erro no overlay (sem refatorar providers agora)
  // @ts-ignore
  if (typeof window !== 'undefined') {
    // @ts-ignore
    (window as any).setCheckoutPaymentErrorMessage = setPaymentErrorMessage;
    // ✅ Ponte mínima: permitir que wallets criem orderResponse e avancem a UI
    // @ts-ignore
    (window as any).setCheckoutOrderResponse = setOrderResponse;
  }

  // Computed values
  const CurrentDesignComponent = DesignDefault;

  // Effects
  // ✅ Carrega produtos da oferta quando shopifyOffer é atualizado
  useEffect(() => {
    if (shopifyOffer) {
      updateOrderSummary();
    }
  }, [shopifyOffer, updateOrderSummary]);

  // ✅ Presença/Ao vivo: cria o cart na primeira interação real do visitante
  // (pointer/tecla/scroll — não no load, pra filtrar bot e preview de link).
  // O backend não dispara recuperação sem email/telefone; isso aqui é só captura.
  const firstInteractionSentRef = useRef(false);
  useEffect(() => {
    if (thankYouData || initialEditingSection) return; // página de obrigado / preview de design não é sessão de checkout
    if (firstInteractionSentRef.current) return;

    const fire = () => {
      if (firstInteractionSentRef.current) return;
      firstInteractionSentRef.current = true;
      cleanup();
      createOrUpdateCartData({
        fullName: formData.userIdentification?.fullName || '',
        email: formData.userIdentification?.email || '',
        phone: formData.userIdentification?.phone || '',
        currentStep: 'IDENTIFICATION',
      })?.catch?.(() => {
        // Falha silenciosa: presença é best-effort, não pode travar o checkout
      });
    };

    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'scroll'];
    const cleanup = () => events.forEach((e) => window.removeEventListener(e, fire));
    events.forEach((e) => window.addEventListener(e, fire, { passive: true }));
    return cleanup;
  }, [thankYouData, initialEditingSection, createOrUpdateCartData, formData.userIdentification]);

  // ✅ Captura de contato no blur (antes do submit da identificação): manda email/telefone
  // válidos pro cart assim que digitados — é o caso mais valioso de recuperação de abandono.
  // O merge no backend garante que nada já salvo é sobrescrito.
  const handleContactCaptured = React.useCallback(
    (data: { fullName?: string; email?: string; phone?: string }) => {
      createOrUpdateCartData({
        fullName: data.fullName || '',
        email: data.email || '',
        phone: data.phone || '',
        currentStep: 'IDENTIFICATION',
      })?.catch?.(() => {
        // Best-effort: falha aqui não pode travar o preenchimento
      });
    },
    [createOrUpdateCartData],
  );

  // ✅ Loading inicial aguarda shopifyOffer E verificação do carrinho
  useEffect(() => {
    if (shopifyOffer && !cartLoading && loading.has('initial')) {
      removeLoading('initial');
    }
  }, [shopifyOffer, cartLoading, loading, removeLoading]);

  // Envia evento de compra uma única vez quando orderResponse é criado
  useEffect(() => {
    if (orderResponse && orderResponse.paymentData?.details) {
      trackPurchase(orderResponse);
    }
  }, [orderResponse?.order.gatewayInfos?.gatewayId, trackPurchase]);

  // ✅ Removido useEffect que definia editingSection - agora é inicializado corretamente no useCheckoutStepper

  const activeEditingSection =
    editingSection ?? (currentStep as DesignViewEditSectionEnum) ?? DesignViewEditSectionEnum.IDENTIFICATION;

  useEffect(() => {
    if (orderResponse) return;

    const previousEditingSection = previousEditingSectionRef.current;
    previousEditingSectionRef.current = activeEditingSection;

    if (!previousEditingSection || previousEditingSection === activeEditingSection) return;

    requestAnimationFrame(() => {
      const stepEl = document.querySelector<HTMLElement>(`main [data-step-section="${activeEditingSection}"]`);
      if (!stepEl) return;

      const topOffset = window.innerHeight * 0.15;
      const targetTop = window.scrollY + stepEl.getBoundingClientRect().top - topOffset;
      const behavior: ScrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

      window.scrollTo({ top: Math.max(targetTop, 0), behavior });
    });
  }, [activeEditingSection, orderResponse]);

  useEffect(() => {
    if (!orderResponse) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [orderResponse]);

  useEffect(() => {
    if (!thankYouData || orderResponse) return;

    const paymentMethodNormalized = String(thankYouData.paymentMethod || '').toLowerCase();
    const paymentMethod = (paymentMethodNormalized === 'pix'
      ? 'pix'
      : paymentMethodNormalized === 'boleto'
      ? 'boleto'
      : 'credit_card') as PaymentMethodEnum;

    const hydratedOrderResponse: CheckoutResponseDto = {
      order: {
        id: Number(thankYouData.orderId || 0),
        status: thankYouData.paymentStatus === 'success' ? ('completed' as any) : ('pending' as any),
        totalPrice: Number(thankYouData.totalPrice || 0),
        totalDiscounts: Number(thankYouData.totalDiscounts || 0),
        items: [],
        customerId: 0,
        storeId: shopifyOffer.store.nanoId ?? shopifyOffer.storeId,
        gatewayInfos: {
          gatewayId: thankYouData.transactionId,
        },
      },
      offer: shopifyOffer.cart,
      customer: {
        id: 0,
        firstName: thankYouData.customerFirstName,
        lastName: thankYouData.customerLastName,
        email: thankYouData.customerEmail,
        phone: '',
        documentNumber: thankYouData.customerDocument,
        storeId: shopifyOffer.store.nanoId ?? shopifyOffer.storeId,
      },
      paymentMethod:
        paymentMethod === 'pix'
          ? ({ paymentMethod: 'pix', pix: { cpf: thankYouData.customerDocument || '' } } as any)
          : paymentMethod === 'boleto'
          ? ({ paymentMethod: 'boleto', boleto: { cpf: thankYouData.customerDocument || '' } } as any)
          : ({
              paymentMethod: 'credit_card',
              creditCard: {
                cardNumber: '',
                expirationDate: '',
                securityCode: '',
                cardHolderName: `${thankYouData.customerFirstName} ${thankYouData.customerLastName}`.trim(),
                cardHolderDocumentNumber: thankYouData.customerDocument || '',
                installments: 1,
              },
            } as any),
      paymentData: thankYouData.paymentData,
      paymentStatus: thankYouData.paymentStatus,
    };

    setOrderResponse(hydratedOrderResponse);
    setEditingSection(null);
  }, [thankYouData, orderResponse, shopifyOffer, setEditingSection]);

  // ✅ Define editingSection baseado no currentStep quando carrinho é carregado
  useEffect(() => {
    if (currentStep && editingSection === null) {
      setEditingSection(currentStep as DesignViewEditSectionEnum);
    }
  }, [currentStep, editingSection, setEditingSection]);

  // ✅ Garante que editingSection seja sempre válido baseado no currentStep
  useEffect(() => {
    if (currentStep && !editingSection) {
      setEditingSection(currentStep as DesignViewEditSectionEnum);
    }
  }, [currentStep, editingSection, setEditingSection]);

  // ✅ Loading unificado - removido loading duplicado do MUI
  // O CheckoutLoading já cuida de todos os estados de loading

  if (!shopifyOffer) {
    return <Alert severity="warning">Não foi possível carregar os dados da oferta.</Alert>;
  }

  const renderActiveView = () => {
    // Se há resposta do pedido, renderiza a view de pagamento
    if (orderResponse) {
      return (
        <CheckoutViews
          orderResponse={orderResponse}
          formData={formData}
          shopifyOffer={shopifyOffer}
          loading={loading}
          setLoading={(newLoading) => {
            // Atualiza o loading state
            Object.keys(newLoading).forEach(key => {
              if (newLoading.has(key)) {
                addLoading(key);
              } else {
                removeLoading(key);
              }
            });
          }}
        />
      );
    }

    // Se não há resposta, renderiza o design atual
    return (
      <CurrentDesignComponent
        formData={formData}
        shopifyOffer={formData.shopifyOffer || shopifyOffer}
        editingSection={activeEditingSection}
        onEdit={handleEditSection}
        onCouponApply={handleCouponApply}
        onPlaceOrder={handlePlaceOrder}
        onSaveSection={handleSaveSection}
        onChangePaymentMethod={handlePaymentMethodChange}
        onCardElementReady={onCardElementReady}
        onReusableToken={onReusableToken}
        hasOneClickUpsell={Boolean(shopifyOffer?.oneClickUpsell)}
        onOrderBumpsChange={handleOrderBumpsChange}
        giftStates={giftStates}
        onApplyGiftCoupon={handleCouponApply}
        onContactCaptured={handleContactCaptured}
        isLoading={loading.has('applyCoupon')}
        checkoutConfig={checkoutConfig}
      />
    );
  };

  return (
    <div
      className="min-h-screen bg-page-bg-light dark:bg-page-bg-dark text-text-primary dark:text-gray-100"
      style={checkoutConfigToCssVars(checkoutConfig)}
    >
      {checkoutConfig && <CheckoutHeader shopifyOffer={shopifyOffer} config={checkoutConfig.header} />}

      {!orderResponse && (
        <Stepper
          steps={steps}
          currentStepId={getCurrentStepId()}
          onStepClick={handleStepClick}
        />
      )}

      <main className="pb-4">
        <CheckoutLoading loading={loading} shopifyOffer={shopifyOffer} paymentErrorMessage={paymentErrorMessage} />
        {renderActiveView()}
      </main>

      {/* Footer - aparece em todas as páginas do checkout */}
      {checkoutConfig && (
        <Footer
          shopifyOffer={shopifyOffer}
          config={checkoutConfig.footer}
          paymentBadgesOverride={paymentBadgesOverride}
          storeContactOverride={storeContactOverride}
        />
      )}

      {/* Popup de upsell one-click: aparece após o cartão aprovado, antes do thank-you */}
      {upsellState && (
        <UpsellPopup
          upsell={upsellState.upsell}
          onAccept={handleUpsellAccept}
          onDecline={handleUpsellDecline}
        />
      )}
    </div>
  );
};

export default CheckoutPage; 